/**
 * Deep-copy a value into plain data ready for a field-masked Firestore write:
 * own enumerable properties only (class getters and methods dropped),
 * `undefined` and functions removed so a merge mask never names a field that
 * carries no value, `Date`s preserved and Firestore `Timestamp`s converted to
 * `Date`s (so a value read back from Firestore round-trips as a timestamp
 * instead of a `{seconds, nanoseconds}` map).
 *
 * Differs from {@link firestoreDeepClone}, which keeps `undefined` (relying on
 * `ignoreUndefinedProperties`) and copies a `Timestamp`'s internals verbatim.
 */
export function toFirestorePlainObject(value: any): any {
    if (value === null || value === undefined) return value;
    if (value instanceof Date) return new Date(value.getTime());
    if (typeof value.toDate === 'function') return value.toDate();
    if (Array.isArray(value)) return value.map(item => toFirestorePlainObject(item));
    if (typeof value === 'object') {
        const result: Record<string, any> = {};
        for (const key of Object.keys(value)) {
            const item = value[key];
            if (item === undefined || typeof item === 'function') continue;
            result[key] = toFirestorePlainObject(item);
        }
        return result;
    }
    return typeof value === 'function' ? undefined : value;
}

export function firestoreDeepClone(obj: any): any {
    if (obj === null || typeof obj !== 'object') {
        return obj;
    }

    if (obj instanceof Date) {
        return new Date(obj.getTime());
    }

    if (Array.isArray(obj)) {
        return obj.map(item => firestoreDeepClone(item));
    }

    const clonedObj: any = {};

    for (const key in obj) {
        if (obj.hasOwnProperty(key)) {
            clonedObj[key] = firestoreDeepClone(obj[key]);
        }
    }

    return clonedObj;
}